import { FaroTextButton } from "@components/common/faro-text-button";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useMarkupContext } from "@context-providers/markup/markup-context";
import { Checkbox, CircularProgress, Divider, FontWeights, Text } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { MarkupsSidePanelAttachmentItem } from "@pages/project-details/project-markups/sidepanel";
import { BulkDeleteMarkupAttachments } from "@pages/project-details/project-markups/sidepanel/attachment/attachment-bulk-actions/bulk-delete-markup-attachments";
import { MarkupUploadAttachment } from "@pages/project-details/project-markups/sidepanel/attachment/markup-upload-attachment";
import { MarkupUploadingAttachment } from "@pages/project-details/project-markups/sidepanel/attachment/markup-uploading-attachment";
import { useRefreshAttachment } from "@pages/project-details/project-markups/sidepanel/use-refresh-attachment";
import { nounPluralize } from "@utils/data-display";
import { AnnotationEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";

/**
 * Component responsible for displaying and managing attachments associated with a markup in the side panel.
 *
 * @param {BaseMarkupProps} props - The properties for the component, including the markup data.
 * @returns {JSX.Element} The rendered component.
 */
export function MarkupsSidePanelAttachments({
  markup
}) {
  const {
    trackEvent
  } = useTrackEvent();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    hasPermissionToEditMarkup,
    projectId,
    attachments
  } = useMarkupContext();
  const {
    refreshAttachmentIfExpired,
    isAttachmentExpired
  } = useRefreshAttachment();
  const [isDownloading, setIsDownloading] = useState(false);
  const numberOfAttachments = attachments.length || 0;
  const [selectedAttachments, setSelectedAttachments] = useState(() => new Set());
  const numberOfSelected = selectedAttachments.size;

  /**
   * Downloads images associated with a markup by creating individual download links for each image.
   * The images are downloaded sequentially using fetch and Blob API.
   * Depending on the user's selection, it either downloads all attachments or only the selected ones.
   *
   * @returns A Promise that resolves once all images are successfully downloaded.
   */
  async function downloadAttachments() {
    if (!numberOfAttachments) {
      return;
    }

    // TODO: https://faro01.atlassian.net/browse/ST-1757 Create a zip file for the multiple images
    try {
      trackEvent({
        name: AnnotationEvents.editAnnotation,
        props: {
          property: isSomeSelected ? "download selected annotation attachments" : "download all annotation attachments",
          isValueEmpty: false
        }
      });
      const attachmentsToDownload = isSomeSelected ? selectedAttachmentObjects : attachments || [];
      if (attachmentsToDownload.length === 0) {
        return;
      }
      setIsDownloading(true);
      const expiredAttachmentIds = attachmentsToDownload.filter(attachment => isAttachmentExpired(attachment)).map(attachment_0 => attachment_0.id);
      let attachmentsToDownloadFinal = attachmentsToDownload;
      if (expiredAttachmentIds.length > 0) {
        try {
          const updatedAttachments = await refreshAttachmentIfExpired(expiredAttachmentIds);
          attachmentsToDownloadFinal = attachmentsToDownload.filter(attachment_1 => !isAttachmentExpired(attachment_1)).concat(updatedAttachments);
        } catch {
          setIsDownloading(false);
          return;
        }
      }
      await Promise.all(attachmentsToDownloadFinal.map(async attachment_2 => {
        const image = attachment_2?.signedUri ?? attachment_2.uri;
        const response = await fetch(image);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement("a");
        downloadLink.href = url;
        downloadLink.download = attachment_2.name;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(url);
      }));
    } catch (error) {
      handleErrorWithToast({
        id: "error when we were downloading the attachments",
        title: "Error with the download",
        error
      });
    } finally {
      setIsDownloading(false);
    }
  }

  /** Handles the change event when the select all checkbox is clicked */
  function handleSelectAllChange() {
    setSelectedAttachments(() => {
      if (isAllSelected || isSomeSelected) {
        return new Set();
      } else {
        return new Set(attachments.map(att => att.id));
      }
    });
  }

  /** Handles the change event when a checkbox for an attachment is clicked */
  function handleAttachmentCheckboxChange(attachmentId, isChecked) {
    setSelectedAttachments(prev => {
      const newSet = new Set(prev);
      if (isChecked) {
        newSet.add(attachmentId);
      } else {
        newSet.delete(attachmentId);
      }
      return newSet;
    });
  }
  const isAllSelected = numberOfSelected === numberOfAttachments && numberOfAttachments > 0;
  const isSomeSelected = numberOfSelected > 0 && numberOfSelected < numberOfAttachments;
  const selectedAttachmentObjects = useMemo(() => {
    return attachments.filter(attachment_3 => selectedAttachments.has(attachment_3.id));
  }, [attachments, selectedAttachments]);
  return <Box sx={{
    position: "relative",
    overflowY: "auto",
    maxHeight: "100%",
    mt: "30px",
    display: "flex",
    flexDirection: "column",
    paddingRight: "15px"
  }}>
      {hasPermissionToEditMarkup && <MarkupUploadAttachment markup={markup} />}

      <Box sx={{
      position: "sticky",
      top: 0,
      zIndex: 10,
      backgroundColor: "background.paper",
      borderBottom: 1,
      borderColor: "divider"
    }}>
        {numberOfAttachments ? <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingTop: "5px",
        paddingBottom: 0
      }}>
            <Box sx={{
          display: "flex",
          alignItems: "center"
        }}>
              <Checkbox checked={isAllSelected} indeterminate={isSomeSelected} onChange={handleSelectAllChange} inputProps={{
            "aria-label": "Select all attachments"
          }}
          // `ml` insets the checkbox from the panel edge; `mr` keeps a gap to the heading.
          controlSx={{
            ml: 1,
            mr: 1
          }} />
              <Text variant="heading14" sx={{
            color: "text.primary"
          }}>
                {nounPluralize({
              counter: numberOfAttachments,
              word: "Attachment"
            })}
              </Text>
            </Box>
            <Box sx={{
          display: "flex",
          alignItems: "center",
          gap: "4px"
        }}>
              {isDownloading ? <CircularProgress size={"1rem"} sx={{
            ml: "5px"
          }} /> : <FaroTextButton onClick={downloadAttachments} sx={{
            fontWeight: FontWeights.SemiBold,
            paddingY: "6px",
            width: "80px",
            color: "primary.main"
          }}>
                  Download
                </FaroTextButton>}
              {hasPermissionToEditMarkup && (isAllSelected || isSomeSelected) && <>
                    <Divider orientation="vertical" flexItem />
                    <BulkDeleteMarkupAttachments selectedAttachments={selectedAttachmentObjects} projectId={projectId} setSelectedAttachments={setSelectedAttachments} />
                  </>}
            </Box>
          </Box> : <Text variant="heading14"
      // The sticky Box around this one is a plain block, so the margin below only
      // applies if this is a block too.
      component="div" sx={{
        color: "text.primary",
        mb: "10px"
      }}>
            No attachments
          </Text>}
      </Box>
      <Box>
        <MarkupUploadingAttachment markupId={markup.id} />
        {attachments && attachments.map(attachment_4 => <MarkupsSidePanelAttachmentItem attachment={attachment_4} key={attachment_4.id} isChecked={selectedAttachments.has(attachment_4.id)} onCheckboxChange={handleAttachmentCheckboxChange} setSelectedAttachments={setSelectedAttachments} />)}
      </Box>
    </Box>;
}